import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Breadcrumbs from '../../../../common/widgets/Breadcrumbs';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { resources } from '../../resources/resources';
import applicationCrumbs from '../ApplicationCrumbs';
import PlatformInformation from '../Common/PlatformInformation';
import MyAccountMenu from '../MyAccount/Common/MyAccountMenu';
import ExclusiveBenefits from './components/ExclusiveBenefits';
import FixedBenefits from './components/FixedBenefits';
import MyBenefitsTabNav from './components/MyBenefitsTabNav';
import Subscriptions from './components/Subscriptions';
import { MyBenefitsNavigationState, MyBenefitsTabTypes } from './types';

interface MyBenefitsState {
  currentTab?: MyBenefitsTabTypes;
}

export default function MyBenefitsPage() {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const crumbs = applicationCrumbs(translate);
  const location = useLocation();
  const navigationState = location.state as MyBenefitsNavigationState | null;

  let currentTab = null;
  if (customerInfo.canAccessSubscriptions) {
    currentTab = MyBenefitsTabTypes.Subscriptions;
  } else if (customerInfo.canAccessMyBenefits) {
    currentTab = MyBenefitsTabTypes.FixedBenefits;
  } else if (customerInfo.canAccessExclusive) {
    currentTab = MyBenefitsTabTypes.ExclusiveBenefits;
  }

  const [state, setState] = useState<MyBenefitsState>({
    currentTab,
  } as MyBenefitsState);

  const onTabChange = (tab: MyBenefitsTabTypes) => {
    setState(
      produce(d => {
        d.currentTab = tab;
      }),
    );
  };

  useEffect(() => {
    if (!navigationState) {
      return;
    }

    if (navigationState.tab) {
      setState(
        produce(d => {
          d.currentTab = navigationState.tab;
        }),
      );
    }
  }, [navigationState]);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.myBenefits]}
      />
      <section className="section-main section-actions my-account-page my-benefits-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.menu.myBenefits)}</h1>
              </div>
              <div className="acc-tabs-container">
                <MyBenefitsTabNav
                  activeTab={state.currentTab!}
                  onTabChange={onTabChange}
                />
              </div>
              <div className="my-benefits-content">
                {customerInfo.canAccessSubscriptions &&
                  state.currentTab === MyBenefitsTabTypes.Subscriptions && (
                    <Subscriptions />
                  )}
                {customerInfo.canAccessMyBenefits &&
                  state.currentTab === MyBenefitsTabTypes.FixedBenefits && (
                    <FixedBenefits />
                  )}
                {customerInfo.canAccessExclusive &&
                  state.currentTab === MyBenefitsTabTypes.ExclusiveBenefits && (
                    <ExclusiveBenefits />
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
